import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Users, Briefcase, FileText, DollarSign, Settings, TrendingUp,
  Search, Trash2, Check, X, Eye, Shield, ShieldCheck, Plus, Award,
  Wallet, ArrowUpRight, ArrowDownRight, LogIn, UserCog, Key,
  MessageCircle, History, Hash, Copy, Upload, Image, Download, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_PERMISSIONS = [
  { id: 'manage_users', label: 'Manage Users' },
  { id: 'manage_posts', label: 'Manage Posts' },
  { id: 'manage_payouts', label: 'Manage Payouts' },
  { id: 'manage_badges', label: 'Manage Badges' },
  { id: 'manage_settings', label: 'Manage Settings' },
  { id: 'view_audit', label: 'View Audit Logs' },
  { id: 'manage_wallets', label: 'Manage Wallets' },
  { id: 'manage_integrations', label: 'Manage Integrations' },
  { id: 'manage_pages', label: 'Manage Pages' },
  { id: 'impersonate_users', label: 'Impersonate Users' }
];

const INTEGRATION_DEFS = {
  stripe: { label: 'Stripe', fields: [{ key: 'api_key', label: 'API Key' }, { key: 'webhook_secret', label: 'Webhook Secret' }] },
  razorpay: { label: 'Razorpay', fields: [{ key: 'key_id', label: 'Key ID' }, { key: 'key_secret', label: 'Key Secret' }] },
  sendgrid: { label: 'SendGrid (Email)', fields: [{ key: 'api_key', label: 'API Key (starts with SG.)' }, { key: 'from_email', label: 'Sender Email' }] },
  msg91: { label: 'MSG91 (SMS & Email OTP)', fields: [{ key: 'auth_key', label: 'Auth Key' }, { key: 'template_id', label: 'SMS OTP Template ID' }, { key: 'email_template_id', label: 'Email OTP Template ID (optional)' }] },
  resend: { label: 'Resend (Email)', fields: [{ key: 'api_key', label: 'API Key' }] },
  gst: { label: 'GST Settings', fields: [{ key: 'gstin', label: 'GSTIN' }, { key: 'sac_code', label: 'SAC Code' }, { key: 'tax_rate', label: 'Tax Rate (%)' }] }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [gigs, setGigs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [adminSettings, setAdminSettings] = useState({});
  const [badges, setBadges] = useState([]);
  const [adminSubUsers, setAdminSubUsers] = useState([]);
  const [integrations, setIntegrations] = useState({});
  const [wallets, setWallets] = useState({ items: [], total: 0 });
  const [userSummary, setUserSummary] = useState(null);
  const [showBadgeDialog, setShowBadgeDialog] = useState(false);
  const [showSubUserDialog, setShowSubUserDialog] = useState(false);
  const [showWalletActionDialog, setShowWalletActionDialog] = useState(null);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [showWalletHistoryDialog, setShowWalletHistoryDialog] = useState(null);
  const [walletHistory, setWalletHistory] = useState([]);
  const [showPostDetail, setShowPostDetail] = useState(null);
  const [showChatDialog, setShowChatDialog] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [newBadge, setNewBadge] = useState({ name: '', description: '', price: '', duration_days: '', color: '#10B981', icon: 'shield-check' });
  const [newSubUser, setNewSubUser] = useState({ email: '', password: '', full_name: '', permissions: {} });
  const [walletAction, setWalletAction] = useState({ amount: '', reason: '', userId: '', type: '' });
  const [integrationEdits, setIntegrationEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [walletSearch, setWalletSearch] = useState('');
  const [cmsPages, setCmsPages] = useState([]);
  const [showPageDialog, setShowPageDialog] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [pageForm, setPageForm] = useState({ title: '', slug: '', content: '', published: false });
  const [logoUploading, setLogoUploading] = useState(false);
  const [kycList, setKycList] = useState([]);
  const [kycFilter, setKycFilter] = useState('pending');
  const [seedingLegal, setSeedingLegal] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
    fetchKyc('pending');
  }, [user]);

  const fetchData = async () => {
    try {
      const promises = [
        axios.get(`${API}/admin/dashboard-stats`).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/users/search?limit=50`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/posts/gigs?limit=50`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/posts/projects?limit=50`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/payouts?limit=50`).catch(() => ({ data: [] })),
        axios.get(`${API}/audit-logs?limit=100`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/settings`).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/badges`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/wallets?page_size=50`).catch(() => ({ data: { items: [], total: 0 } })),
        axios.get(`${API}/admin/pages`).catch(() => ({ data: [] }))
      ];
      if (isSuperAdmin) {
        promises.push(axios.get(`${API}/admin/sub-users`).catch(() => ({ data: [] })));
        promises.push(axios.get(`${API}/admin/integrations`).catch(() => ({ data: {} })));
      }
      const results = await Promise.all(promises);
      setStats(results[0].data);
      setUsersList(results[1].data.users || results[1].data);
      setGigs(results[2].data);
      setProjects(results[3].data);
      setPayouts(results[4].data);
      setAuditLogs(results[5].data);
      setAdminSettings(results[6].data);
      setBadges(results[7].data);
      setWallets(results[8].data);
      setCmsPages(results[9].data);
      if (isSuperAdmin && results[10]) setAdminSubUsers(results[10].data);
      if (isSuperAdmin && results[11]) setIntegrations(results[11].data);
    } catch (error) {
      console.error('Failed to fetch admin data', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUser = async (userId, verified) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/verify?verified=${verified}`);
      toast.success(`User ${verified ? 'verified' : 'unverified'}`);
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const fetchKyc = async (status = kycFilter) => {
    try {
      const res = await axios.get(`${API}/admin/kyc?status=${status}`);
      setKycList(res.data);
    } catch (e) { console.error('Failed to fetch KYC', e); }
  };
  const handleVerifyKyc = async (userId) => {
    try {
      await axios.post(`${API}/admin/kyc/${userId}/verify`);
      toast.success('KYC verified');
      fetchKyc();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleRejectKyc = async (userId) => {
    const reason = window.prompt('Rejection reason:', 'Documents could not be verified');
    if (reason === null) return;
    try {
      await axios.post(`${API}/admin/kyc/${userId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('KYC rejected');
      fetchKyc();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleSeedLegal = async () => {
    setSeedingLegal(true);
    try {
      const res = await axios.post(`${API}/admin/seed-legal-pages`);
      toast.success(res.data.message || 'Legal pages updated');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to seed legal pages'); }
    finally { setSeedingLegal(false); }
  };
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try { await axios.delete(`${API}/admin/users/${userId}`); toast.success('User deleted'); fetchData(); } catch (e) { toast.error('Failed'); }
  };
  const handleDeleteGig = async (gigId) => {
    if (!window.confirm('Delete this gig?')) return;
    try { await axios.delete(`${API}/admin/posts/gigs/${gigId}`); toast.success('Gig deleted'); fetchData(); } catch (e) { toast.error('Failed'); }
  };
  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Delete this project?')) return;
    try { await axios.delete(`${API}/admin/posts/projects/${projectId}`); toast.success('Project deleted'); fetchData(); } catch (e) { toast.error('Failed'); }
  };
  const handleApproveGig = async (gigId) => {
    try { await axios.patch(`${API}/admin/posts/gigs/${gigId}/approve`); toast.success('Gig approved and live!'); fetchData(); } catch (e) { toast.error('Failed to approve gig'); }
  };
  const handleRejectGig = async (gigId) => {
    const reason = window.prompt('Rejection reason:');
    if (reason === null) return;
    try { await axios.patch(`${API}/admin/posts/gigs/${gigId}/reject?reason=${encodeURIComponent(reason || 'Does not meet guidelines')}`); toast.success('Gig rejected'); fetchData(); } catch (e) { toast.error('Failed'); }
  };
  const handleApproveProject = async (projectId) => {
    try { await axios.patch(`${API}/admin/posts/projects/${projectId}/approve`); toast.success('Project approved and live!'); fetchData(); } catch (e) { toast.error('Failed to approve project'); }
  };
  const handleRejectProject = async (projectId) => {
    const reason = window.prompt('Rejection reason:');
    if (reason === null) return;
    try { await axios.patch(`${API}/admin/posts/projects/${projectId}/reject?reason=${encodeURIComponent(reason || 'Does not meet guidelines')}`); toast.success('Project rejected'); fetchData(); } catch (e) { toast.error('Failed'); }
  };
  const handleSeedData = async () => {
    if (!window.confirm('This will add dummy users, gigs, projects, bids and reviews. Continue?')) return;
    try {
      const res = await axios.post(`${API}/admin/seed-data`, {});
      const s = res.data.summary;
      toast.success(`Created: ${s.users_created} users, ${s.gigs_created} gigs, ${s.projects_created} projects, ${s.reviews_created} reviews`);
      fetchData();
    } catch (e) { toast.error('Failed to seed data'); }
  };
  const handleBulkUpload = async (type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.csv';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        let items;
        if (file.name.endsWith('.csv')) {
          const lines = text.trim().split('\n');
          const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
          items = lines.slice(1).map(line => {
            const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
            const obj = {};
            headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
            if (obj.tags) obj.tags = obj.tags.split(';');
            if (obj.skills_required) obj.skills_required = obj.skills_required.split(';');
            if (obj.price) obj.price = parseFloat(obj.price);
            if (obj.budget_min) obj.budget_min = parseFloat(obj.budget_min);
            if (obj.budget_max) obj.budget_max = parseFloat(obj.budget_max);
            if (obj.delivery_days) obj.delivery_days = parseInt(obj.delivery_days);
            return obj;
          });
        } else {
          const parsed = JSON.parse(text);
          items = Array.isArray(parsed) ? parsed : parsed[type] || parsed.items || [parsed];
        }
        const body = { [type]: items };
        const res = await axios.post(`${API}/admin/bulk/${type}`, body);
        toast.success(`Bulk ${type}: ${res.data.created} created, ${res.data.errors} errors`);
        if (res.data.errors > 0) { console.warn('Bulk errors:', res.data.error_details); }
        fetchData();
      } catch (err) { toast.error(`Failed to process ${file.name}: ${err.message}`); }
    };
    input.click();
  };
  const downloadCsvTemplate = (type) => {
    const templates = {
      users: 'email,full_name,role,password\njohn@example.com,John Doe,freelancer,SecurePass@123\njane@example.com,Jane Smith,client,SecurePass@123',
      gigs: 'title,description,category,price,delivery_days,tags\nProfessional Logo Design,I will design a modern logo for your brand,Graphic Design,5000,3,logo;branding;design\nReact Web App,Full-stack React application development,Web Development,25000,14,react;node;mongodb',
      projects: 'title,description,category,budget_min,budget_max,skills_required\nE-commerce Platform,Build a complete online store with payments,Web Development,50000,100000,React;Node.js;MongoDB\nMobile Fitness App,iOS and Android fitness tracking app,Mobile Development,75000,150000,Flutter;Firebase;REST API'
    };
    const blob = new Blob([templates[type]], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const handleSearchUsers = async () => {
    try { const res = await axios.get(`${API}/admin/users/search?query=${searchQuery}&limit=50`); setUsersList(res.data.users || res.data); } catch (e) { toast.error('Failed'); }
  };
  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData(e.target);
      const body = {};
      if (formData.get('platform_fee')) body.platform_fee_percentage = parseFloat(formData.get('platform_fee'));
      if (formData.get('site_name')) body.site_name = formData.get('site_name');
      await axios.patch(`${API}/admin/settings`, body);
      toast.success('Settings updated'); fetchData();
    } catch (e) { toast.error('Failed'); }
  };
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2MB'); return; }
    setLogoUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const uploadRes = await axios.post(`${API}/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const logoUrl = uploadRes.data.url;
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`;
      await axios.patch(`${API}/admin/settings`, { logo_url: fullUrl });
      toast.success('Logo updated!');
      fetchData();
    } catch (err) { toast.error('Failed to upload logo'); }
    setLogoUploading(false);
  };
  const handleRemoveLogo = async () => {
    try {
      await axios.patch(`${API}/admin/settings`, { logo_url: '' });
      toast.success('Logo removed');
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };
  const handleCreateBadge = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/badges`, { ...newBadge, price: parseFloat(newBadge.price), duration_days: parseInt(newBadge.duration_days) });
      toast.success('Badge created!'); setShowBadgeDialog(false); setNewBadge({ name: '', description: '', price: '', duration_days: '', color: '#10B981', icon: 'shield-check' }); fetchData();
    } catch (e) { toast.error('Failed to create badge'); }
  };
  const handleDeleteBadge = async (badgeId) => {
    if (!window.confirm('Delete this badge?')) return;
    try { await axios.delete(`${API}/admin/badges/${badgeId}`); toast.success('Badge deleted'); fetchData(); } catch (e) { toast.error('Failed'); }
  };
  const handleImpersonate = async (userId) => {
    if (!window.confirm('This will log you in as this user. Continue?')) return;
    try {
      const res = await axios.post(`${API}/admin/impersonate/${userId}`);
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      window.location.href = '/dashboard';
    } catch (e) { toast.error('Failed to impersonate'); }
  };
  const handleViewSummary = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/summary`);
      setUserSummary(res.data);
      setShowSummaryDialog(true);
    } catch (e) { toast.error('Failed to load summary'); }
  };
  const handleCreateSubUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/sub-users`, newSubUser);
      toast.success('Admin sub-user created!');
      setShowSubUserDialog(false);
      setNewSubUser({ email: '', password: '', full_name: '', permissions: {} });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleDeleteSubUser = async (subId) => {
    if (!window.confirm('Delete this admin sub-user?')) return;
    try { await axios.delete(`${API}/admin/sub-users/${subId}`); toast.success('Sub-user deleted'); fetchData(); } catch (e) { toast.error('Failed'); }
  };
  const handleSaveIntegration = async (name) => {
    try {
      await axios.put(`${API}/admin/integrations/${name}`, { credentials: integrationEdits[name] || {} });
      toast.success(`${name} credentials updated!`);
      setIntegrationEdits(prev => ({ ...prev, [name]: {} }));
      fetchData();
    } catch (e) { toast.error('Failed to update'); }
  };
  const handleWalletAction = async (e) => {
    e.preventDefault();
    const { userId, type, amount, reason } = walletAction;
    try {
      await axios.post(`${API}/admin/wallets/${userId}/${type}`, { amount: parseFloat(amount), reason });
      toast.success(`Wallet ${type}ed`);
      setShowWalletActionDialog(null);
      setWalletAction({ amount: '', reason: '', userId: '', type: '' });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleSearchWallets = async () => {
    try {
      const res = await axios.get(`${API}/admin/wallets?search=${walletSearch}&page_size=50`);
      setWallets(res.data);
    } catch (e) { toast.error('Failed'); }
  };

  // Feature 3: View wallet transaction history
  const handleViewWalletHistory = async (userId, userName) => {
    try {
      const res = await axios.get(`${API}/admin/wallets/${userId}/transactions?limit=100`);
      setWalletHistory(res.data);
      setShowWalletHistoryDialog({ user_id: userId, user_name: userName });
    } catch (e) { toast.error('Failed to load history'); }
  };

  // Feature 2: View post detail
  const handleViewPostDetail = async (type, id) => {
    try {
      const endpoint = type === 'gig' ? `${API}/admin/posts/gigs/${id}` : `${API}/admin/posts/projects/${id}/detail`;
      const res = await axios.get(endpoint);
      setShowPostDetail({ type, ...res.data });
    } catch (e) { toast.error('Failed to load details'); }
  };

  // Feature 6: Admin chat initiate
  const handleAdminChat = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim() || !showChatDialog) return;
    try {
      await axios.post(`${API}/admin/chat/initiate`, { user_id: showChatDialog.id, message: chatMessage.trim() });
      toast.success(`Message sent to ${showChatDialog.full_name}`);
      setChatMessage('');
      setShowChatDialog(null);
    } catch (e) { toast.error('Failed to send message'); }
  };

  // CMS Page handlers
  const openPageEditor = (page = null) => {
    if (page) {
      setEditingPage(page);
      setPageForm({ title: page.title, slug: page.slug, content: page.content, published: page.published });
    } else {
      setEditingPage(null);
      setPageForm({ title: '', slug: '', content: '', published: false });
    }
    setShowPageDialog(true);
  };
  const handleSavePage = async (e) => {
    e.preventDefault();
    try {
      if (editingPage) {
        await axios.put(`${API}/admin/pages/${editingPage.id}`, pageForm);
        toast.success('Page updated!');
      } else {
        await axios.post(`${API}/admin/pages`, pageForm);
        toast.success('Page created!');
      }
      setShowPageDialog(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save page'); }
  };
  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Delete this page?')) return;
    try { await axios.delete(`${API}/admin/pages/${pageId}`); toast.success('Page deleted'); fetchData(); } catch (e) { toast.error('Failed'); }
  };
  const handleTogglePublish = async (page) => {
    try {
      await axios.put(`${API}/admin/pages/${page.id}`, { title: page.title, slug: page.slug, content: page.content, published: !page.published });
      toast.success(page.published ? 'Page unpublished' : 'Page published!');
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  // Feature 4: Granular permissions helpers
  const toggleSubUserPerm = (permId, accessType) => {
    setNewSubUser(prev => {
      const perms = { ...prev.permissions };
      if (!perms[permId]) perms[permId] = { read: false, write: false };
      perms[permId] = { ...perms[permId], [accessType]: !perms[permId][accessType] };
      // If write is on, read must be on
      if (accessType === 'write' && perms[permId].write) perms[permId].read = true;
      // If read is off, write must be off
      if (accessType === 'read' && !perms[permId].read) perms[permId].write = false;
      // Remove permission entirely if both are false
      if (!perms[permId].read && !perms[permId].write) delete perms[permId];
      return { ...prev, permissions: perms };
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  // Helper to render permissions display
  const renderPermBadges = (perms) => {
    if (!perms) return null;
    // Handle legacy list format
    if (Array.isArray(perms)) {
      return perms.map(p => <Badge key={p} variant="secondary" className="text-xs mr-1 mb-1">{p.replace(/_/g, ' ')}</Badge>);
    }
    // New dict format
    return Object.entries(perms).map(([key, val]) => {
      const access = [];
      if (val.read) access.push('R');
      if (val.write) access.push('W');
      if (access.length === 0) return null;
      return <Badge key={key} variant="secondary" className="text-xs mr-1 mb-1">{key.replace(/_/g, ' ')} [{access.join('/')}]</Badge>;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">Loading admin panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary" data-testid="admin-title">Admin Panel</h1>
            {isSuperAdmin && <Badge className="bg-amber-500">Super Admin</Badge>}
          </div>
          <p className="text-muted-foreground mt-1">Manage your platform</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Users</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{stats?.total_users || 0}</div></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Active Gigs</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{stats?.total_gigs || 0}</div></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Projects</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{stats?.total_projects || 0}</div></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Revenue</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{(stats?.total_revenue || stats?.total_gmv || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</div></CardContent></Card>
        </div>

        <Tabs defaultValue="users">
          <TabsList className="flex flex-wrap gap-1 h-auto">
            <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
            <TabsTrigger value="kyc" data-testid="kyc-tab">KYC</TabsTrigger>
            <TabsTrigger value="posts" data-testid="posts-tab">Posts</TabsTrigger>
            <TabsTrigger value="wallets" data-testid="wallets-tab">Wallets</TabsTrigger>
            <TabsTrigger value="badges" data-testid="badges-tab">Badges</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="payouts-tab">Payouts</TabsTrigger>
            <TabsTrigger value="audit" data-testid="audit-tab">Audit</TabsTrigger>
            {isSuperAdmin && <TabsTrigger value="sub-admins" data-testid="sub-admins-tab">Sub-Admins</TabsTrigger>}
            {isSuperAdmin && <TabsTrigger value="integrations" data-testid="integrations-tab">Integrations</TabsTrigger>}
            <TabsTrigger value="pages" data-testid="pages-tab">Pages</TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">Settings</TabsTrigger>
          </TabsList>

          {/* ===== USERS TAB (Feature 1: user_code + Feature 6: Chat) ===== */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> User Management</CardTitle>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name, email or user ID (TP-...)..." className="max-w-sm" data-testid="user-search" onKeyDown={e => e.key === 'Enter' && handleSearchUsers()} />
                  <Button onClick={handleSearchUsers} size="sm"><Search className="w-4 h-4" /></Button>
                  <Button onClick={() => handleBulkUpload('users')} size="sm" variant="outline" data-testid="bulk-upload-users"><Upload className="w-4 h-4 mr-1" /> Bulk Import</Button>
                  <Button onClick={() => downloadCsvTemplate('users')} size="sm" variant="ghost" className="text-blue-600" data-testid="csv-template-users"><Download className="w-4 h-4 mr-1" /> CSV Template</Button>
                  <Button onClick={handleSeedData} size="sm" variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-50" data-testid="seed-data-btn"><Plus className="w-4 h-4 mr-1" /> Seed Demo Data</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User ID</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Verified</TableHead><TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usersList.map(u => (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded font-mono" data-testid={`user-code-${u.id}`}>{u.user_code || '---'}</code>
                              {u.user_code && <button onClick={() => copyToClipboard(u.user_code)} className="text-muted-foreground hover:text-primary"><Copy className="w-3 h-3" /></button>}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{u.full_name}</TableCell>
                          <TableCell className="text-sm">{u.email}</TableCell>
                          <TableCell><Badge variant="outline" className="capitalize">{u.role}</Badge></TableCell>
                          <TableCell>{u.verified ? <Check className="w-4 h-4 text-green-500" /> : <X className="w-4 h-4 text-red-500" />}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => handleViewSummary(u.id)} data-testid={`summary-${u.id}`} title="View Summary"><Eye className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => setShowChatDialog(u)} data-testid={`chat-${u.id}`} title="Chat with user"><MessageCircle className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => handleImpersonate(u.id)} data-testid={`impersonate-${u.id}`} title="Login as user"><LogIn className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => handleVerifyUser(u.id, !u.verified)} data-testid={`verify-${u.id}`}><ShieldCheck className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteUser(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== POSTS TAB (Feature 2: task_code + detail view) ===== */}
          <TabsContent value="kyc">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap justify-between items-center gap-3">
                  <div><CardTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> KYC Verification</CardTitle><CardDescription>Review and verify freelancer PAN &amp; Aadhaar submissions (Hitch ID)</CardDescription></div>
                  <div className="flex gap-1">
                    {['pending', 'verified', 'rejected', 'all'].map(s => (
                      <Button key={s} size="sm" variant={kycFilter === s ? 'default' : 'outline'} onClick={() => { setKycFilter(s); fetchKyc(s); }} data-testid={`kyc-filter-${s}`} className="capitalize">{s}</Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {kycList.length === 0 ? <p className="text-center text-muted-foreground py-8" data-testid="kyc-empty">No {kycFilter !== 'all' ? kycFilter : ''} KYC submissions</p> : (
                  <div className="space-y-3">
                    {kycList.map(k => (
                      <div key={k.id} className="flex flex-wrap items-center justify-between gap-3 py-3 border-b last:border-0" data-testid={`kyc-row-${k.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <h4 className="font-medium">{k.full_name}</h4>
                            <Badge variant="outline" className="text-xs font-mono">{k.hitch_id || '—'}</Badge>
                            <Badge className={`text-xs ${k.kyc_status === 'verified' ? 'bg-green-100 text-green-700' : k.kyc_status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{k.kyc_status}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">{k.email} · PAN: <span className="font-mono">{k.pan_number || '—'}</span> · Aadhaar: <span className="font-mono">{k.aadhaar_number || '—'}</span></p>
                        </div>
                        {k.kyc_status === 'pending' && (
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleVerifyKyc(k.id)} data-testid={`kyc-verify-${k.id}`}><Check className="w-4 h-4 mr-1" /> Verify</Button>
                            <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleRejectKyc(k.id)} data-testid={`kyc-reject-${k.id}`}><X className="w-4 h-4 mr-1" /> Reject</Button>
                          </div>
                        )}
                        {k.kyc_status === 'verified' && (
                          <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleRejectKyc(k.id)} data-testid={`kyc-revoke-${k.id}`}><X className="w-4 h-4 mr-1" /> Revoke</Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="posts">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Gigs ({gigs.length})</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => handleBulkUpload('gigs')} size="sm" variant="outline" data-testid="bulk-upload-gigs"><Upload className="w-3 h-3 mr-1" /> Bulk Import</Button>
                      <Button onClick={() => downloadCsvTemplate('gigs')} size="sm" variant="ghost" className="text-blue-600" data-testid="csv-template-gigs"><Download className="w-3 h-3 mr-1" /> CSV Template</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="max-h-[500px] overflow-y-auto">
                  {gigs.map(g => (
                    <div key={g.id} className="flex justify-between items-center py-3 border-b last:border-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {g.task_code && <code className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-mono" data-testid={`gig-code-${g.id}`}>{g.task_code}</code>}
                          <p className="text-sm font-medium truncate">{g.title}</p>
                          <Badge variant="outline" className={
                            g.status === 'pending_approval' ? 'bg-amber-100 text-amber-700 border-amber-300 text-[10px]' :
                            g.status === 'active' ? 'bg-green-100 text-green-700 border-green-300 text-[10px]' :
                            'text-[10px]'
                          }>
                            {g.status === 'pending_approval' ? 'Pending' : g.status === 'active' ? 'Live' : g.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {g.price?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                          <span className="mx-1">·</span>
                          <span className="font-medium text-primary/70">by {g.freelancer_name}</span>
                        </p>
                      </div>
                      <div className="flex gap-1 ml-2 flex-shrink-0">
                        {g.status === 'pending_approval' && (
                          <>
                            <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleApproveGig(g.id)} data-testid={`approve-gig-${g.id}`} title="Approve"><Check className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleRejectGig(g.id)} data-testid={`reject-gig-${g.id}`} title="Reject"><X className="w-4 h-4" /></Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleViewPostDetail('gig', g.id)} data-testid={`gig-detail-${g.id}`} title="View details"><Eye className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteGig(g.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                  {gigs.length === 0 && <p className="text-center text-muted-foreground py-8">No gigs</p>}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Projects ({projects.length})</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => handleBulkUpload('projects')} size="sm" variant="outline" data-testid="bulk-upload-projects"><Upload className="w-3 h-3 mr-1" /> Bulk Import</Button>
                      <Button onClick={() => downloadCsvTemplate('projects')} size="sm" variant="ghost" className="text-blue-600" data-testid="csv-template-projects"><Download className="w-3 h-3 mr-1" /> CSV Template</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="max-h-[500px] overflow-y-auto">
                  {projects.map(p => (
                    <div key={p.id} className="flex justify-between items-center py-3 border-b last:border-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {p.task_code && <code className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-mono" data-testid={`project-code-${p.id}`}>{p.task_code}</code>}
                          <p className="text-sm font-medium truncate">{p.title}</p>
                          <Badge variant="outline" className={
                            p.status === 'pending_approval' ? 'bg-amber-100 text-amber-700 border-amber-300 text-[10px]' :
                            p.status === 'open' ? 'bg-green-100 text-green-700 border-green-300 text-[10px]' :
                            'text-[10px]'
                          }>
                            {p.status === 'pending_approval' ? 'Pending' : p.status === 'open' ? 'Live' : p.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {p.budget_min?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })} - {p.budget_max?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                          <span className="mx-1">·</span>
                          <span className="font-medium text-primary/70">by {p.client_name}</span>
                        </p>
                      </div>
                      <div className="flex gap-1 ml-2 flex-shrink-0">
                        {p.status === 'pending_approval' && (
                          <>
                            <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleApproveProject(p.id)} data-testid={`approve-project-${p.id}`} title="Approve"><Check className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleRejectProject(p.id)} data-testid={`reject-project-${p.id}`} title="Reject"><X className="w-4 h-4" /></Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleViewPostDetail('project', p.id)} data-testid={`project-detail-${p.id}`} title="View details"><Eye className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteProject(p.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                  {projects.length === 0 && <p className="text-center text-muted-foreground py-8">No projects</p>}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ===== WALLETS TAB (Feature 3: Wallet History) ===== */}
          <TabsContent value="wallets">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Wallet className="w-5 h-5" /> Wallet Management</CardTitle>
                <div className="flex gap-2 mt-2">
                  <Input value={walletSearch} onChange={e => setWalletSearch(e.target.value)} placeholder="Search by user name or email..." className="max-w-sm" data-testid="wallet-search" onKeyDown={e => e.key === 'Enter' && handleSearchWallets()} />
                  <Button onClick={handleSearchWallets} size="sm"><Search className="w-4 h-4" /></Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Balance</TableHead><TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(wallets.items || []).map(w => (
                        <TableRow key={w.id}>
                          <TableCell className="font-medium">{w.user_name}</TableCell>
                          <TableCell>{w.user_email}</TableCell>
                          <TableCell><Badge variant="outline" className="capitalize">{w.user_role}</Badge></TableCell>
                          <TableCell className="font-bold">{w.balance?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" onClick={() => handleViewWalletHistory(w.user_id, w.user_name)} data-testid={`wallet-history-${w.user_id}`} title="Transaction History">
                                <History className="w-3 h-3 mr-1" /> History
                              </Button>
                              <Button size="sm" variant="outline" className="text-green-600" onClick={() => { setWalletAction({ amount: '', reason: '', userId: w.user_id, type: 'credit' }); setShowWalletActionDialog(w); }} data-testid={`credit-${w.user_id}`}>
                                <ArrowUpRight className="w-3 h-3 mr-1" /> Credit
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => { setWalletAction({ amount: '', reason: '', userId: w.user_id, type: 'debit' }); setShowWalletActionDialog(w); }} data-testid={`debit-${w.user_id}`}>
                                <ArrowDownRight className="w-3 h-3 mr-1" /> Debit
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {(wallets.items || []).length === 0 && <p className="text-center text-muted-foreground py-8">No wallets found.</p>}
              </CardContent>
            </Card>
          </TabsContent>

          {/* BADGES TAB */}
          <TabsContent value="badges">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div><CardTitle className="flex items-center gap-2"><Award className="w-5 h-5" /> Badge Management</CardTitle><CardDescription>Create and manage skill verification badges</CardDescription></div>
                  <Dialog open={showBadgeDialog} onOpenChange={setShowBadgeDialog}>
                    <DialogTrigger asChild><Button data-testid="create-badge-button"><Plus className="w-4 h-4 mr-2" /> Create Badge</Button></DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Create Badge</DialogTitle><DialogDescription>Freelancers can purchase this to verify their skills</DialogDescription></DialogHeader>
                      <form onSubmit={handleCreateBadge} className="space-y-4" data-testid="badge-create-form">
                        <div><Label>Name</Label><Input value={newBadge.name} onChange={e => setNewBadge({...newBadge, name: e.target.value})} required placeholder="Verified Expert" data-testid="badge-name" /></div>
                        <div><Label>Description</Label><Input value={newBadge.description} onChange={e => setNewBadge({...newBadge, description: e.target.value})} required data-testid="badge-description" /></div>
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label>Price</Label><Input type="number" value={newBadge.price} onChange={e => setNewBadge({...newBadge, price: e.target.value})} required data-testid="badge-price" /></div>
                          <div><Label>Duration (days)</Label><Input type="number" value={newBadge.duration_days} onChange={e => setNewBadge({...newBadge, duration_days: e.target.value})} required data-testid="badge-duration" /></div>
                        </div>
                        <div><Label>Color</Label><div className="flex gap-2 mt-1">{['#10B981','#3B82F6','#F59E0B','#EF4444','#8B5CF6','#EC4899'].map(c=><button key={c} type="button" onClick={()=>setNewBadge({...newBadge,color:c})} className={`w-8 h-8 rounded-full border-2 ${newBadge.color===c?'border-primary scale-110':'border-transparent'}`} style={{backgroundColor:c}} />)}</div></div>
                        <Button type="submit" className="w-full" data-testid="badge-submit">Create Badge</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {badges.length === 0 ? <p className="text-center text-muted-foreground py-8">No badges yet</p> : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {badges.map(b => (
                      <Card key={b.id} className="border-2" data-testid={`admin-badge-${b.id}`}>
                        <CardContent className="pt-4 flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: b.color+'20'}}><ShieldCheck className="w-5 h-5" style={{color:b.color}} /></div>
                          <div className="flex-1"><h3 className="font-bold">{b.name}</h3><p className="text-sm text-muted-foreground">{b.description}</p><div className="flex gap-2 mt-1"><Badge variant="secondary">{b.price?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</Badge><span className="text-xs text-muted-foreground">{b.duration_days}d</span></div></div>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteBadge(b.id)}><Trash2 className="w-4 h-4" /></Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PAYOUTS TAB */}
          <TabsContent value="payouts">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5" /> Payouts</CardTitle></CardHeader>
              <CardContent>
                {payouts.length === 0 ? <p className="text-center text-muted-foreground py-8">No payouts yet</p> : (
                  <Table><TableHeader><TableRow><TableHead>User</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                    <TableBody>{payouts.map(p => (<TableRow key={p.id}><TableCell>{p.freelancer_name || p.user_id?.slice(0,8)}</TableCell><TableCell>{p.amount?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</TableCell><TableCell><Badge variant={p.status==='completed'?'default':'secondary'}>{p.status}</Badge></TableCell><TableCell>{new Date(p.created_at).toLocaleDateString('en-IN')}</TableCell></TableRow>))}</TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AUDIT TAB */}
          <TabsContent value="audit">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Audit Trail</CardTitle></CardHeader>
              <CardContent>
                <div className="max-h-[500px] overflow-y-auto space-y-2">
                  {auditLogs.map(log => (
                    <div key={log.id} className="flex items-start gap-3 py-2 border-b last:border-0">
                      <div className="w-2 h-2 mt-2 rounded-full bg-secondary flex-shrink-0" />
                      <div className="flex-1"><p className="text-sm font-medium">{log.action?.replace(/_/g,' ')}</p><p className="text-xs text-muted-foreground">{log.user_name} - {log.resource_type} {log.resource_id ? `#${log.resource_id.slice(0,8)}` : ''}</p><p className="text-xs text-muted-foreground">{new Date(log.created_at).toLocaleString('en-IN')}</p></div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== SUB-ADMINS TAB (Feature 4: Granular Permissions) ===== */}
          {isSuperAdmin && (
            <TabsContent value="sub-admins">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div><CardTitle className="flex items-center gap-2"><UserCog className="w-5 h-5" /> Admin Sub-Users</CardTitle><CardDescription>Create admin accounts with granular read/write permissions</CardDescription></div>
                    <Dialog open={showSubUserDialog} onOpenChange={setShowSubUserDialog}>
                      <DialogTrigger asChild><Button data-testid="create-sub-admin-button"><Plus className="w-4 h-4 mr-2" /> Create Sub-Admin</Button></DialogTrigger>
                      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>Create Admin Sub-User</DialogTitle><DialogDescription>Grant specific read/write permissions per feature</DialogDescription></DialogHeader>
                        <form onSubmit={handleCreateSubUser} className="space-y-4" data-testid="sub-admin-form">
                          <div><Label>Full Name</Label><Input value={newSubUser.full_name} onChange={e => setNewSubUser({...newSubUser, full_name: e.target.value})} required data-testid="sub-admin-name" /></div>
                          <div><Label>Email</Label><Input type="email" value={newSubUser.email} onChange={e => setNewSubUser({...newSubUser, email: e.target.value})} required data-testid="sub-admin-email" /></div>
                          <div><Label>Password</Label><Input type="password" value={newSubUser.password} onChange={e => setNewSubUser({...newSubUser, password: e.target.value})} required data-testid="sub-admin-password" /></div>
                          <div>
                            <Label className="mb-3 block">Feature Permissions</Label>
                            <div className="border rounded-lg overflow-hidden">
                              <div className="grid grid-cols-[1fr,60px,60px] bg-slate-50 px-3 py-2 text-xs font-medium text-muted-foreground border-b">
                                <span>Feature</span><span className="text-center">Read</span><span className="text-center">Write</span>
                              </div>
                              {ADMIN_PERMISSIONS.map(p => {
                                const current = newSubUser.permissions[p.id] || { read: false, write: false };
                                return (
                                  <div key={p.id} className="grid grid-cols-[1fr,60px,60px] px-3 py-2 border-b last:border-0 items-center hover:bg-slate-50/50" data-testid={`perm-row-${p.id}`}>
                                    <span className="text-sm">{p.label}</span>
                                    <div className="flex justify-center">
                                      <Checkbox
                                        checked={current.read}
                                        onCheckedChange={() => toggleSubUserPerm(p.id, 'read')}
                                        data-testid={`perm-${p.id}-read`}
                                      />
                                    </div>
                                    <div className="flex justify-center">
                                      <Checkbox
                                        checked={current.write}
                                        onCheckedChange={() => toggleSubUserPerm(p.id, 'write')}
                                        data-testid={`perm-${p.id}-write`}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <Button type="submit" className="w-full" data-testid="sub-admin-submit">Create Sub-Admin</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  {adminSubUsers.length === 0 ? <p className="text-center text-muted-foreground py-8">No sub-admins created yet</p> : (
                    <div className="space-y-3">
                      {adminSubUsers.map(sub => (
                        <Card key={sub.id} className="border" data-testid={`sub-admin-${sub.id}`}>
                          <CardContent className="pt-4 flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">{sub.full_name?.charAt(0)}</div>
                            <div className="flex-1">
                              <h3 className="font-bold">{sub.full_name}</h3>
                              <p className="text-sm text-muted-foreground">{sub.email}</p>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {renderPermBadges(sub.permissions)}
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteSubUser(sub.id)} data-testid={`delete-sub-admin-${sub.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* INTEGRATIONS TAB */}
          {isSuperAdmin && (
            <TabsContent value="integrations">
              <div className="space-y-6">
                <h2 className="text-xl font-heading font-bold flex items-center gap-2"><Key className="w-5 h-5" /> Integration Credentials</h2>
                <p className="text-sm text-muted-foreground">Manage API keys and credentials for third-party services.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(INTEGRATION_DEFS).map(([name, def]) => {
                    const saved = integrations[name];
                    const edits = integrationEdits[name] || {};
                    return (
                      <Card key={name} data-testid={`integration-${name}`}>
                        <CardHeader>
                          <CardTitle className="text-base">{def.label}</CardTitle>
                          {saved?.updated_by_name && <CardDescription>Last updated by {saved.updated_by_name}</CardDescription>}
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {def.fields.map(f => (
                            <div key={f.key}>
                              <Label className="text-xs">{f.label}</Label>
                              <Input
                                type="text"
                                placeholder={saved?.credentials?.[f.key] || 'Not set'}
                                value={edits[f.key] || ''}
                                onChange={e => setIntegrationEdits(prev => ({ ...prev, [name]: { ...prev[name], [f.key]: e.target.value } }))}
                                data-testid={`integration-${name}-${f.key}`}
                              />
                            </div>
                          ))}
                          <Button size="sm" onClick={() => handleSaveIntegration(name)} className="w-full" data-testid={`save-integration-${name}`}>Save {def.label}</Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </TabsContent>
          )}

          {/* ===== CMS PAGES TAB ===== */}
          <TabsContent value="pages">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div><CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Content Pages</CardTitle><CardDescription>Create and manage custom pages (About, Terms, FAQ, etc.)</CardDescription></div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleSeedLegal} disabled={seedingLegal} data-testid="seed-legal-button">{seedingLegal ? 'Updating...' : 'Update Legal Pages'}</Button>
                    <Button onClick={() => openPageEditor()} data-testid="create-page-button"><Plus className="w-4 h-4 mr-2" /> Create Page</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {cmsPages.length === 0 ? <p className="text-center text-muted-foreground py-8">No pages created yet</p> : (
                  <div className="space-y-3">
                    {cmsPages.map(pg => (
                      <div key={pg.id} className="flex items-center justify-between py-3 border-b last:border-0" data-testid={`cms-page-${pg.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h4 className="font-medium">{pg.title}</h4>
                            <Badge variant={pg.published ? 'default' : 'secondary'} className="text-xs">{pg.published ? 'Published' : 'Draft'}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">/page/{pg.slug} · Updated {new Date(pg.updated_at).toLocaleDateString('en-IN')} · by {pg.author_name}</p>
                        </div>
                        <div className="flex gap-1 ml-2">
                          <Button size="sm" variant="ghost" onClick={() => handleTogglePublish(pg)} data-testid={`toggle-publish-${pg.id}`} title={pg.published ? 'Unpublish' : 'Publish'}>
                            {pg.published ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openPageEditor(pg)} data-testid={`edit-page-${pg.id}`}><Eye className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeletePage(pg.id)} data-testid={`delete-page-${pg.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Branding / Logo */}
              <Card data-testid="branding-card">
                <CardHeader><CardTitle className="flex items-center gap-2"><Image className="w-5 h-5" /> Branding</CardTitle><CardDescription>Customize your platform logo and name</CardDescription></CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm mb-2 block">Site Logo</Label>
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center overflow-hidden bg-slate-50 flex-shrink-0">
                        {adminSettings?.logo_url ? (
                          <img src={adminSettings.logo_url} alt="Logo" className="w-full h-full object-contain p-2" data-testid="current-logo-preview" />
                        ) : (
                          <Image className="w-8 h-8 text-muted-foreground/40" />
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="cursor-pointer">
                          <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-upload-input" disabled={logoUploading} />
                          <span className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${logoUploading ? 'opacity-50 cursor-wait bg-muted' : 'hover:bg-accent cursor-pointer'}`}>
                            <Upload className="w-4 h-4" /> {logoUploading ? 'Uploading...' : 'Upload Logo'}
                          </span>
                        </label>
                        {adminSettings?.logo_url && (
                          <Button size="sm" variant="ghost" className="text-red-500 justify-start w-fit" onClick={handleRemoveLogo} data-testid="remove-logo-btn">
                            <X className="w-3 h-3 mr-1" /> Remove Logo
                          </Button>
                        )}
                        <p className="text-xs text-muted-foreground">PNG, JPG, WebP. Max 2MB. Recommended: 200x60px</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Platform Settings */}
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Settings className="w-5 h-5" /> Platform Settings</CardTitle></CardHeader>
                <CardContent>
                  <form onSubmit={handleUpdateSettings} className="space-y-4" data-testid="settings-form">
                    <div><Label>Site Name</Label><Input name="site_name" placeholder={adminSettings?.site_name || 'TaskPiece'} data-testid="site-name-input" /></div>
                    <div><Label>Platform Fee (%)</Label><Input name="platform_fee" type="number" step="0.1" defaultValue={adminSettings?.platform_fee_percentage || 5} data-testid="platform-fee" /></div>
                    <p className="text-xs text-muted-foreground">Razorpay credentials are managed in the <strong>Integrations</strong> tab.</p>
                    <Button type="submit" data-testid="save-settings">Save Settings</Button>
                  </form>
                </CardContent>
              </Card>

              {/* Tracking & Analytics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Tracking & Analytics</CardTitle>
                  <CardDescription>Manage Facebook Pixel and Google Analytics. Changes take effect immediately on all pages.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const fd = new FormData(e.target);
                    const body = {};
                    const fbId = fd.get('fb_pixel_id')?.trim();
                    const gaId = fd.get('ga_measurement_id')?.trim();
                    if (fbId !== undefined) body.fb_pixel_id = fbId;
                    if (gaId !== undefined) body.ga_measurement_id = gaId;
                    try {
                      await axios.patch(`${API}/admin/settings`, body);
                      toast.success('Tracking settings saved! Changes will reflect on next page load.');
                      fetchData();
                    } catch (err) { toast.error('Failed to save tracking settings'); }
                  }} className="space-y-4" data-testid="tracking-form">
                    <div>
                      <Label htmlFor="fb_pixel_id">Facebook Pixel ID</Label>
                      <Input id="fb_pixel_id" name="fb_pixel_id" placeholder="e.g. 1234567890123456" defaultValue={adminSettings?.fb_pixel_id || ''} data-testid="fb-pixel-input" className="mt-1.5 font-mono" />
                      <p className="text-xs text-muted-foreground mt-1">Find this in your Facebook Events Manager → Data Sources → Pixel.</p>
                    </div>
                    <div>
                      <Label htmlFor="ga_measurement_id">Google Analytics Measurement ID</Label>
                      <Input id="ga_measurement_id" name="ga_measurement_id" placeholder="e.g. G-XXXXXXXXXX" defaultValue={adminSettings?.ga_measurement_id || ''} data-testid="ga-id-input" className="mt-1.5 font-mono" />
                      <p className="text-xs text-muted-foreground mt-1">Find this in Google Analytics → Admin → Data Streams → Measurement ID.</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button type="submit" data-testid="save-tracking">Save Tracking Settings</Button>
                      {(adminSettings?.fb_pixel_id || adminSettings?.ga_measurement_id) && (
                        <span className="text-xs text-green-600 font-medium">Active</span>
                      )}
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* ===== DIALOGS ===== */}

        {/* User Summary Dialog */}
        <Dialog open={showSummaryDialog} onOpenChange={setShowSummaryDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>User Summary</DialogTitle></DialogHeader>
            {userSummary && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-xl font-bold text-primary">{userSummary.user?.full_name?.charAt(0)}</div>
                  <div>
                    <h3 className="font-heading font-bold text-lg">{userSummary.user?.full_name}</h3>
                    <p className="text-sm text-muted-foreground">{userSummary.user?.email}</p>
                    <div className="flex gap-2 mt-1">
                      <Badge className="capitalize">{userSummary.user?.role}</Badge>
                      {userSummary.user?.user_code && <Badge variant="outline" className="font-mono">{userSummary.user.user_code}</Badge>}
                      <Badge variant="outline">{userSummary.stats?.member_since_days} days</Badge>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3" data-testid="user-summary-stats">
                  <div className="text-center p-3 rounded bg-slate-50"><div className="text-xl font-bold text-primary">{userSummary.stats?.gigs_posted}</div><div className="text-xs text-muted-foreground">Gigs</div></div>
                  <div className="text-center p-3 rounded bg-slate-50"><div className="text-xl font-bold text-primary">{userSummary.stats?.projects_posted}</div><div className="text-xs text-muted-foreground">Projects</div></div>
                  <div className="text-center p-3 rounded bg-slate-50"><div className="text-xl font-bold text-primary">{userSummary.stats?.bids_placed}</div><div className="text-xs text-muted-foreground">Bids</div></div>
                  <div className="text-center p-3 rounded bg-green-50"><div className="text-xl font-bold text-green-600">{userSummary.stats?.total_earned?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</div><div className="text-xs text-muted-foreground">Earned</div></div>
                  <div className="text-center p-3 rounded bg-blue-50"><div className="text-xl font-bold text-blue-600">{userSummary.stats?.total_spent?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</div><div className="text-xs text-muted-foreground">Spent</div></div>
                  <div className="text-center p-3 rounded bg-slate-50"><div className="text-xl font-bold text-primary">{userSummary.stats?.wallet_balance?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</div><div className="text-xs text-muted-foreground">Wallet</div></div>
                  <div className="text-center p-3 rounded bg-slate-50"><div className="text-xl font-bold text-primary">{userSummary.stats?.active_contracts}</div><div className="text-xs text-muted-foreground">Active</div></div>
                  <div className="text-center p-3 rounded bg-slate-50"><div className="text-xl font-bold text-primary">{userSummary.stats?.completed_contracts}</div><div className="text-xs text-muted-foreground">Completed</div></div>
                  <div className="text-center p-3 rounded bg-slate-50"><div className="text-xl font-bold text-primary">{userSummary.stats?.messages_sent}</div><div className="text-xs text-muted-foreground">Messages</div></div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Wallet Action Dialog */}
        <Dialog open={!!showWalletActionDialog} onOpenChange={() => setShowWalletActionDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{walletAction.type === 'credit' ? 'Credit' : 'Debit'} Wallet</DialogTitle>
              <DialogDescription>For user: {showWalletActionDialog?.user_name} ({showWalletActionDialog?.user_email})</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleWalletAction} className="space-y-4" data-testid="wallet-action-form">
              <div><Label>Amount</Label><Input type="number" value={walletAction.amount} onChange={e => setWalletAction({...walletAction, amount: e.target.value})} required min="1" data-testid="wallet-amount" /></div>
              <div><Label>Reason</Label><Input value={walletAction.reason} onChange={e => setWalletAction({...walletAction, reason: e.target.value})} required placeholder="e.g. Bonus payment" data-testid="wallet-reason" /></div>
              <Button type="submit" className={`w-full ${walletAction.type === 'debit' ? 'bg-red-600 hover:bg-red-700' : ''}`} data-testid="wallet-action-submit">
                {walletAction.type === 'credit' ? 'Credit' : 'Debit'} Wallet
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Feature 3: Wallet History Dialog */}
        <Dialog open={!!showWalletHistoryDialog} onOpenChange={() => setShowWalletHistoryDialog(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5" /> Wallet History</DialogTitle>
              <DialogDescription>Transaction history for {showWalletHistoryDialog?.user_name}</DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[50vh]">
              {walletHistory.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No transactions found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Reason</TableHead><TableHead>By</TableHead><TableHead>Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {walletHistory.map(txn => (
                      <TableRow key={txn.id} data-testid={`txn-${txn.id}`}>
                        <TableCell className="text-xs">{new Date(txn.created_at).toLocaleString('en-IN')}</TableCell>
                        <TableCell>
                          <Badge variant={txn.type === 'credit' ? 'default' : 'destructive'} className="text-xs">
                            {txn.type === 'credit' ? '+' : '-'}
                          </Badge>
                        </TableCell>
                        <TableCell className={`font-bold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {txn.type === 'credit' ? '+' : '-'}{txn.amount?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell className="text-sm max-w-[150px] truncate">{txn.reason}</TableCell>
                        <TableCell className="text-xs">{txn.performed_by_name}</TableCell>
                        <TableCell className="font-medium">{txn.balance_after?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Feature 2: Post Detail Dialog */}
        <Dialog open={!!showPostDetail} onOpenChange={() => setShowPostDetail(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{showPostDetail?.type === 'gig' ? 'Gig' : 'Project'} Details</DialogTitle>
            </DialogHeader>
            {showPostDetail?.type === 'gig' && showPostDetail?.gig && (
              <div className="space-y-4" data-testid="gig-detail-view">
                <div className="flex items-center gap-3">
                  {showPostDetail.gig.task_code && <code className="text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded font-mono">{showPostDetail.gig.task_code}</code>}
                  <h3 className="text-lg font-bold">{showPostDetail.gig.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground">{showPostDetail.gig.description}</p>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-muted-foreground">Category</Label><p className="font-medium">{showPostDetail.gig.category}</p></div>
                  <div><Label className="text-xs text-muted-foreground">Price</Label><p className="font-medium">{showPostDetail.gig.price?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p></div>
                  <div><Label className="text-xs text-muted-foreground">Delivery</Label><p className="font-medium">{showPostDetail.gig.delivery_days} days</p></div>
                  <div><Label className="text-xs text-muted-foreground">Status</Label><p><Badge variant="outline">{showPostDetail.gig.status}</Badge></p></div>
                  <div><Label className="text-xs text-muted-foreground">Orders</Label><p className="font-medium">{showPostDetail.total_orders}</p></div>
                  <div><Label className="text-xs text-muted-foreground">Reviews</Label><p className="font-medium">{showPostDetail.reviews_count}</p></div>
                </div>
                {showPostDetail.gig.tags?.length > 0 && (
                  <div><Label className="text-xs text-muted-foreground">Tags</Label><div className="flex flex-wrap gap-1 mt-1">{showPostDetail.gig.tags.map(t => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}</div></div>
                )}
                {showPostDetail.freelancer && (
                  <div className="pt-3 border-t">
                    <Label className="text-xs text-muted-foreground">Freelancer</Label>
                    <p className="font-medium">{showPostDetail.freelancer.full_name} ({showPostDetail.freelancer.email})</p>
                  </div>
                )}
              </div>
            )}
            {showPostDetail?.type === 'project' && showPostDetail?.project && (
              <div className="space-y-4" data-testid="project-detail-view">
                <div className="flex items-center gap-3">
                  {showPostDetail.project.task_code && <code className="text-sm bg-emerald-50 text-emerald-700 px-2 py-1 rounded font-mono">{showPostDetail.project.task_code}</code>}
                  <h3 className="text-lg font-bold">{showPostDetail.project.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground">{showPostDetail.project.description}</p>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-muted-foreground">Category</Label><p className="font-medium">{showPostDetail.project.category}</p></div>
                  <div><Label className="text-xs text-muted-foreground">Budget</Label><p className="font-medium">{showPostDetail.project.budget_min?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })} - {showPostDetail.project.budget_max?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p></div>
                  <div><Label className="text-xs text-muted-foreground">Status</Label><p><Badge variant="outline">{showPostDetail.project.status}</Badge></p></div>
                  <div><Label className="text-xs text-muted-foreground">Bids</Label><p className="font-medium">{showPostDetail.bids?.length || 0}</p></div>
                </div>
                {showPostDetail.project.skills_required?.length > 0 && (
                  <div><Label className="text-xs text-muted-foreground">Skills</Label><div className="flex flex-wrap gap-1 mt-1">{showPostDetail.project.skills_required.map(s => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>)}</div></div>
                )}
                {showPostDetail.client && (
                  <div className="pt-3 border-t">
                    <Label className="text-xs text-muted-foreground">Client</Label>
                    <p className="font-medium">{showPostDetail.client.full_name} ({showPostDetail.client.email})</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Feature 6: Admin Chat Dialog */}
        <Dialog open={!!showChatDialog} onOpenChange={() => { setShowChatDialog(null); setChatMessage(''); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><MessageCircle className="w-5 h-5" /> Send Message</DialogTitle>
              <DialogDescription>
                Start a chat with {showChatDialog?.full_name} ({showChatDialog?.email})
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdminChat} className="space-y-4" data-testid="admin-chat-form">
              <div>
                <Label>Message</Label>
                <textarea
                  value={chatMessage}
                  onChange={e => setChatMessage(e.target.value)}
                  required
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring mt-1.5"
                  placeholder="Type your message..."
                  data-testid="admin-chat-input"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1" data-testid="admin-chat-send">Send Message</Button>
                <Button type="button" variant="outline" onClick={() => { navigate('/messages'); }} data-testid="admin-go-messages">Open Messages</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* CMS Page Editor Dialog */}
        <Dialog open={showPageDialog} onOpenChange={setShowPageDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPage ? 'Edit Page' : 'Create Page'}</DialogTitle>
              <DialogDescription>Manage content pages for your platform</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSavePage} className="space-y-4" data-testid="page-editor-form">
              <div>
                <Label>Title</Label>
                <Input value={pageForm.title} onChange={e => {
                  const title = e.target.value;
                  setPageForm(prev => ({ ...prev, title, slug: editingPage ? prev.slug : title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') }));
                }} required data-testid="page-title-input" placeholder="About Us" />
              </div>
              <div>
                <Label>Slug (URL path)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground">/page/</span>
                  <Input value={pageForm.slug} onChange={e => setPageForm({ ...pageForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} required data-testid="page-slug-input" placeholder="about-us" />
                </div>
              </div>
              <div>
                <Label>Content (HTML supported)</Label>
                <textarea
                  value={pageForm.content}
                  onChange={e => setPageForm({ ...pageForm, content: e.target.value })}
                  required
                  rows={12}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring mt-1.5 font-mono text-xs"
                  placeholder="<h2>About Us</h2><p>Welcome to TaskPiece...</p>"
                  data-testid="page-content-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={pageForm.published} onCheckedChange={checked => setPageForm({ ...pageForm, published: !!checked })} data-testid="page-published-checkbox" />
                <Label>Published (visible to public)</Label>
              </div>
              <Button type="submit" className="w-full" data-testid="page-save-btn">{editingPage ? 'Update Page' : 'Create Page'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
